import { z } from 'zod';
import { apiCall } from '@/util/api';

export const DEFAULT_BUCKET_WINDOW_SECONDS = 86_400; // 1 day

const ImpressionPointSchema = z.object({
  sentAt: z.string().datetime({ offset: true }),
  cumulative: z.number().int().nonnegative(),
});

const BucketPointSchema = z.object({
  midpoint: z.string().datetime({ offset: true }),
  impressions: z.number().int().nonnegative(),
  clicks: z.number().int().nonnegative(),
  ctr: z.number().min(0),
});

export const EngagementMetricsSchema = z.object({
  impressionsTimeline: z.array(ImpressionPointSchema),
  directUserImpressionsTimeline: z.array(ImpressionPointSchema),
  directAndIndirectImpressionsTimeline: z.array(ImpressionPointSchema),
  ctrTimeline: z.array(BucketPointSchema),
  directCtrTimeline: z.array(BucketPointSchema),
  directAndIndirectCtrTimeline: z.array(BucketPointSchema),
  currentUserLegBuckets: z.array(BucketPointSchema),
  bucketWindowSeconds: z.number().int().positive(),
});

export type EngagementMetrics = z.infer<typeof EngagementMetricsSchema>;
export type EngagementImpressionPoint = z.infer<typeof ImpressionPointSchema>;
export type EngagementBucketPoint = z.infer<typeof BucketPointSchema>;

export interface NivoLinePoint {
  x: Date;
  y: number;
}

export interface NivoLineSeries {
  id: string;
  data: NivoLinePoint[];
}

export interface EngagementChartData {
  impressionsSeries: {
    total: NivoLineSeries;
    direct: NivoLineSeries;
    snowballed: NivoLineSeries;
  };
  ctrSeries: {
    all: NivoLineSeries;
    direct: NivoLineSeries;
    snowballed: NivoLineSeries;
  };
  markers: { x: Date }[];
}

const impressionToPoint = (p: EngagementImpressionPoint): NivoLinePoint => ({
  x: new Date(p.sentAt),
  y: p.cumulative,
});

const ctrBucketToPoint = (p: EngagementBucketPoint): NivoLinePoint => ({
  x: new Date(p.midpoint),
  y: p.ctr,
});

export const EngagementMetricsLogic = {
  async fetch(
    topicId: string,
    bucketWindowSeconds: number = DEFAULT_BUCKET_WINDOW_SECONDS,
    signal?: AbortSignal
  ): Promise<EngagementMetrics> {
    const encoded = encodeURIComponent(topicId);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const qs = `?bucketWindowSecs=${encodeURIComponent(String(bucketWindowSeconds))}&tz=${encodeURIComponent(tz)}`;
    const response = await apiCall(
      `/api/topics/${encoded}/engagement-metrics${qs}`,
      { signal }
    );
    if (!response.ok) {
      throw new Error(`Failed to load engagement metrics (${response.status})`);
    }
    const data = await response.json();
    return EngagementMetricsSchema.parse(data);
  },

  formatBucketWindow(seconds: number): string {
    if (seconds % 86_400 === 0 && seconds >= 86_400) {
      const days = seconds / 86_400;
      return `${days}-day window`;
    }
    if (seconds % 3_600 === 0 && seconds >= 3_600) {
      const hours = seconds / 3_600;
      return `${hours}-hour window`;
    }
    const minutes = Math.max(1, Math.round(seconds / 60));
    return `${minutes}-min window`;
  },

  toChartData(metrics: EngagementMetrics): EngagementChartData {
    return {
      impressionsSeries: {
        total: {
          id: 'Msgs sent by all users',
          data: metrics.impressionsTimeline.map(impressionToPoint),
        },
        direct: {
          id: 'Msgs you sent',
          data: metrics.directUserImpressionsTimeline.map(impressionToPoint),
        },
        snowballed: {
          id: 'Msgs you or your downhill contacts sent',
          data: metrics.directAndIndirectImpressionsTimeline.map(
            impressionToPoint
          ),
        },
      },
      ctrSeries: {
        all: {
          id: 'On msgs sent by all users',
          data: metrics.ctrTimeline.map(ctrBucketToPoint),
        },
        direct: {
          id: 'On msgs you sent',
          data: metrics.directCtrTimeline.map(ctrBucketToPoint),
        },
        snowballed: {
          id: 'On msgs you or your downhill contacts sent',
          data: metrics.directAndIndirectCtrTimeline.map(ctrBucketToPoint),
        },
      },
      markers: metrics.currentUserLegBuckets.map(b => ({
        x: new Date(b.midpoint),
      })),
    };
  },
};
